import { useEffect, useMemo, useRef, useState } from 'react';
import { type GridSortModel } from '@mui/x-data-grid';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DeviceType,
  SortOrder,
  WASHBOARD_SORTABLE_FIELDS,
  type WashboardSortableField,
} from 'api/admin/washboard/washboard';
import { useStoresForBusiness } from 'api/queries/useStoresForBusiness';
import { useWashboardDevices } from 'api/queries/useWashboardDevices';

export const DEFAULT_PAGE_SIZE = 20;

export const DEVICE_TYPE_OPTIONS = [
  { display: 'Penny', value: DeviceType.PENNY },
  { display: 'Pulse', value: DeviceType.PULSE },
  { display: 'Dime', value: DeviceType.DIME },
];

const getErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  const apiError = error as {
    response?: { data?: { error?: string } };
    message?: string;
  };

  return apiError.response?.data?.error ?? apiError.message ?? null;
};

function parseIntParam(params: URLSearchParams, key: string, fallback: 0): number;
function parseIntParam(params: URLSearchParams, key: string, fallback: null): number | null;
function parseIntParam(params: URLSearchParams, key: string, fallback: 0 | null): number | null {
  const n = parseInt(params.get(key) ?? '', 10);
  return isNaN(n) ? fallback : n;
}

export function useWashboardFilters() {
  const history = useHistory();
  const location = useLocation();
  const initialParams = new URLSearchParams(location.search);

  const [pageNumber, setPageNumber] = useState(() => parseIntParam(initialParams, 'page', 0));
  const [pageSize, setPageSize] = useState(() => {
    const parsed = parseIntParam(initialParams, 'pageSize', null);
    return parsed !== null && parsed >= 1 ? parsed : DEFAULT_PAGE_SIZE;
  });
  const [searchTerm, setSearchTerm] = useState(() => initialParams.get('search') ?? '');
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(() =>
    parseIntParam(initialParams, 'businessId', null),
  );
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(() =>
    parseIntParam(initialParams, 'storeId', null),
  );
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType | null>(() => {
    const paramsDeviceType = initialParams.get('deviceType');
    const isValidDeviceType = (Object.values(DeviceType) as string[]).includes(
      paramsDeviceType ?? '',
    );
    return isValidDeviceType ? (paramsDeviceType as DeviceType) : null;
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    const sortBy = initialParams.get('sortBy');
    const sortOrder = initialParams.get('sortOrder');
    const validSortBy = sortBy && (WASHBOARD_SORTABLE_FIELDS as readonly string[]).includes(sortBy);
    const validSortOrder = sortOrder === SortOrder.ASC || sortOrder === SortOrder.DESC;
    return validSortBy && validSortOrder ? [{ field: sortBy, sort: sortOrder as SortOrder }] : [];
  });
  const previousBusinessIdRef = useRef<number | null>(selectedBusinessId);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedBusinessId) params.set('businessId', String(selectedBusinessId));
    if (selectedStoreId) params.set('storeId', String(selectedStoreId));
    if (selectedDeviceType) params.set('deviceType', selectedDeviceType);
    if (pageNumber > 0) params.set('page', String(pageNumber));
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pageSize));
    if (sortModel.length > 0) {
      params.set('sortBy', sortModel[0].field);
      if (sortModel[0].sort) params.set('sortOrder', sortModel[0].sort);
    }
    const qs = params.toString();
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [
    searchTerm,
    selectedBusinessId,
    selectedStoreId,
    selectedDeviceType,
    pageNumber,
    pageSize,
    sortModel,
  ]);

  useEffect(() => {
    if (previousBusinessIdRef.current !== selectedBusinessId) {
      setSelectedStoreId(null);
      previousBusinessIdRef.current = selectedBusinessId;
    }
  }, [selectedBusinessId]);

  const {
    data: stores = [],
    isLoading: isStoresLoading,
    error: storesError,
  } = useStoresForBusiness(selectedBusinessId ?? 0, Boolean(selectedBusinessId));

  const storeOptions = useMemo(
    () =>
      (stores ?? []).map((store) => ({
        value: store.id,
        display: `${store.name} [ID: ${store.id}]`,
      })),
    [stores],
  );

  const selectedStoreOption = storeOptions.find((o) => o.value === selectedStoreId);
  const selectedDeviceTypeOption = DEVICE_TYPE_OPTIONS.find((o) => o.value === selectedDeviceType);

  const storeId = selectedStoreId ? Number(selectedStoreId) : undefined;
  const businessId = storeId ? undefined : selectedBusinessId ?? undefined;
  const deviceType = selectedDeviceType || undefined;
  const searchTermParam = searchTerm.trim() || undefined;
  const sortBy = sortModel[0]?.field as WashboardSortableField | undefined;
  const sortOrder = (sortModel[0]?.sort as SortOrder) || undefined;

  const {
    data,
    isLoading: isDevicesLoading,
    error: devicesError,
    refetch,
  } = useWashboardDevices({
    page: pageNumber + 1,
    limit: pageSize,
    businessId,
    storeId,
    deviceType,
    searchTerm: searchTermParam,
    sortBy,
    sortOrder,
  });
  const isLoading = isStoresLoading || isDevicesLoading;

  const handlePageChange = ({ page }: { page: number }) => {
    setPageNumber(page);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setPageNumber(0);
    if (!model.length) {
      setSortModel([]);
      return;
    }
    const [{ field, sort }] = model;
    setSortModel([{ field, sort }]);
  };

  const setSearchTermAndResetPage = (value: string) => {
    if (value === searchTerm) {
      return;
    }
    setPageNumber(0);
    setSearchTerm(value);
  };

  const setSelectedBusinessIdAndResetPage = (value: number | null) => {
    if (value === selectedBusinessId) {
      return;
    }
    setPageNumber(0);
    setSelectedBusinessId(value);
  };

  const setSelectedStoreIdAndResetPage = (value: number | null) => {
    if (value === selectedStoreId) {
      return;
    }
    setPageNumber(0);
    setSelectedStoreId(value);
  };

  const setSelectedDeviceTypeAndResetPage = (value: DeviceType | null) => {
    if (value === selectedDeviceType) {
      return;
    }
    setPageNumber(0);
    setSelectedDeviceType(value);
  };

  const setPageSizeAndResetPage = (value: number) => {
    if (!Number.isInteger(value) || value <= 0 || value === pageSize) {
      return;
    }
    setPageNumber(0);
    setPageSize(value);
  };

  return {
    pageNumber,
    pageSize,
    searchTerm,
    setSearchTerm: setSearchTermAndResetPage,
    selectedBusinessId,
    setSelectedBusinessId: setSelectedBusinessIdAndResetPage,
    setSelectedStoreId: setSelectedStoreIdAndResetPage,
    setSelectedDeviceType: setSelectedDeviceTypeAndResetPage,
    sortModel,
    storeOptions,
    selectedStoreOption,
    selectedDeviceTypeOption,
    devices: data?.devices ?? [],
    total: data?.total ?? 0,
    isLoading,
    storesError: getErrorMessage(storesError),
    devicesError: getErrorMessage(devicesError),
    refetch,
    setPageNumber,
    setPageSize: setPageSizeAndResetPage,
    handlePageChange,
    handleSortModelChange,
  };
}
